import React, { useState, useContext, useEffect } from "react";
import userDetail from "Stores/userDetail";
import { AccountsListingContext } from "../AccountsList";
import MultiSelectDropdown from "Pages/Dashboard/contacts/ContactFilter/MultiSelectDropdown";
import ContactFieldFilter from "Pages/Dashboard/contacts/ContactFilter/ContactFieldFilter";
import ContactDateFilter from "Pages/Dashboard/contacts/ContactFilter/ContactDateFilter";
import ContactMetricsFilter from "Pages/Dashboard/contacts/ContactFilter/ContactMetricsFilter";
import makeApi, { URLS } from "Utils/apiURL";
import {
  EmailFilterList,
  CallsFilterList,
  TasksFilterList,
  AccountFieldsList,
  ContactsFilterList,
  ACCOUNT_CUSTOM_FILTERS_ENABLED
} from "../AccountConstants";
import FooterAction from "./FooterAction";
import HeaderAction from "./HeaderAction";
import useDebouncedApply from "Utils/hooks/useDebouncedApply";

export const Main = ({
  applyFilterCbk = () => {},
  resetFilterCbk = () => {}
}) => {
  const {
    data = {},
    filterList = [],
    setFilterList = () => {},
    setData = {},
    showAccountFilter = false,
    setShowAccountFilter = () => {}
  } = useContext(AccountsListingContext);

  const [activeDropdown, setActiveDropdown] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [disableContactFieldAddBtn, setDisableContactFieldAddBtn] =
    useState(true);
  const [accountCustomLabels, setAccountCustomLabels] = useState({});

  useEffect(() => {
    if (!ACCOUNT_CUSTOM_FILTERS_ENABLED) return;
    (async () => {
      try {
        const r = await makeApi({ url: URLS.accountCustomFields, method: "GET" });
        if (r?.status === 200 && r.data) setAccountCustomLabels(r.data);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const { customFieldsCount = 0 } = userDetail?.userFeatureFlag || {};
  const customFieldOptions = ACCOUNT_CUSTOM_FILTERS_ENABLED
    ? Array.from({ length: customFieldsCount }, (value, index) => {
        const key = `customField${index + 1}`;
        return {
          value: accountCustomLabels[key] || `Custom Field ${index + 1}`,
          subKey: key.toUpperCase(),
          key
        };
      })
    : [];

  const clearAllFilter = async () => {
    if (filterList?.length > 0) {
      let value = resetFilterCbk();
      if (value) {
        applyFilterCbk({
          pageNo: 1,
          searchTerm: "",
          limit: 10,
          filterPayload: {
            stageId: ""
          }
        });
      }
    }
  };

  const cancelFilter = () => {
    // Filters auto-apply as you edit, so Close just dismisses the panel;
    // "Clear all" remains the explicit reset.
    setShowAccountFilter(!showAccountFilter);
  };

  const handleActiveFilter = (value = "") => {
    setActiveDropdown(value === activeDropdown ? "" : value);
  };

  const hasFilters = filterList?.length > 0;
  const actionList = [
    {
      className: `clearAllFilter${hasFilters ? "" : " disabled"}`,
      method: hasFilters ? clearAllFilter : () => {},
      name: "Clear All",
      id: "clearAllFilter"
    },
    {
      className: "cancelFilter",
      method: cancelFilter,
      name: "Close",
      id: "cancelFilter"
    }
  ];

  const getSelectedItems = (item = []) => {
    if (item?.length > 0) {
      return item.filter(item => item?.checked) || [];
    }
    return [];
  };

  // Handle Change Action
  const setFiterUsed = (isValidList = false, key = "") => {
    if (key) {
      let tempList = [...filterList];
      if (isValidList && !tempList.includes(key)) {
        tempList.push(key);
      } else if (!isValidList && filterList?.length > 0) {
        tempList = tempList.filter(
          item => item?.toLowerCase() !== key?.toLowerCase()
        );
      }
      setFilterList(tempList);
    }
  };

  // Only fully-formed conditions count as "used" — same rule drives the
  // auto-apply signature so a half-built row never fires a query.
  const getValidItems = (key = "", value = []) => {
    switch (key.toLowerCase()) {
      case "accountfields":
      case "tasks":
      case "calls":
      case "emails":
      case "contacts":
        return (value || []).filter(
          item =>
            item?.fieldName && item?.label && item?.operator && item?.value
        );
      case "owners":
        return (value || []).filter(item => item?.checked);
      case "dates":
        return (value || []).filter(
          item => item?.fieldName && item?.fromDate && item?.toDate
        );
      default:
        return value || [];
    }
  };

  const getSelectedFilterKey = (key = "", value = []) => {
    if (key && value?.length > 0) {
      setFiterUsed(getValidItems(key, value).length > 0, key);
    }
  };

  // Auto-apply: re-query (debounced) whenever the committed conditions change.
  const buildAppliedSignature = () =>
    JSON.stringify(
      Object.keys(data || {}).reduce((acc, key) => {
        acc[key] = getValidItems(key, data[key]);
        return acc;
      }, {})
    );
  useDebouncedApply(buildAppliedSignature(), () => applyFilterCbk());

  const validateData = (data = []) => {
    let validData = false;
    const dataLength = data?.length;
    if (dataLength) {
      const { fieldName = "", value = "" } = data?.[dataLength - 1] || {};
      validData = fieldName && value;
    }
    return validData;
  };

  const handleFilterObject = (key = "", value = []) => {
    if (key) {
      getSelectedFilterKey(key, value);
      let tempObj = { ...data, [key]: value };
      setData({ ...tempObj });
      const validData = validateData(tempObj?.[key]);
      setDisableContactFieldAddBtn(!validData);
    }
  };

  const filterOption = [
    {
      label: "Account Field",
      keyValue: "accountFields",
      data: data?.accountFields,
      activeFilter: activeDropdown,
      setActiveFilter: handleActiveFilter,
      setAction: handleFilterObject,
      errMsg: errMsg,
      setErrMsg: setErrMsg,
      type: "multiFieldSelect",
      customConditionalFieldList: [...AccountFieldsList, ...customFieldOptions],
      disableAddButton: disableContactFieldAddBtn
    },
    {
      label: "Owners",
      keyValue: "owners",
      placeholder: "Select",
      selected: getSelectedItems(data?.owners),
      data: data?.owners || [],
      setAction: handleFilterObject,
      activeFilter: activeDropdown,
      setActiveFilter: handleActiveFilter,
      type: "multiSelectApply",
      count: 2
    },
    {
      label: "Date",
      keyValue: "dates",
      data: data?.dates || [],
      activeFilter: activeDropdown,
      setActiveFilter: handleActiveFilter,
      setAction: handleFilterObject,
      errMsg: errMsg,
      setErrMsg: setErrMsg,
      type: "multiDateSelect"
    },
    {
      label: "Contacts",
      keyValue: "contacts",
      data: data?.contacts,
      activeFilter: activeDropdown,
      setActiveFilter: handleActiveFilter,
      setAction: handleFilterObject,
      errMsg: errMsg,
      setErrMsg: setErrMsg,
      type: "multiFieldNumberSelect",
      customConditionFieldnameList: [...ContactsFilterList]
    },
    {
      label: "Emails",
      keyValue: "emails",
      data: data?.emails,
      activeFilter: activeDropdown,
      setActiveFilter: handleActiveFilter,
      setAction: handleFilterObject,
      errMsg: errMsg,
      setErrMsg: setErrMsg,
      type: "multiFieldNumberSelect",
      customConditionFieldnameList: [...EmailFilterList],
      disableAddButton: disableContactFieldAddBtn
    },
    {
      label: "Calls",
      keyValue: "calls",
      data: data?.calls,
      activeFilter: activeDropdown,
      setActiveFilter: handleActiveFilter,
      setAction: handleFilterObject,
      errMsg: errMsg,
      setErrMsg: setErrMsg,
      type: "multiFieldNumberSelect",
      customConditionFieldnameList: [...CallsFilterList],
      disableAddButton: disableContactFieldAddBtn
    },
    {
      label: "Tasks",
      keyValue: "tasks",
      data: data?.tasks,
      activeFilter: activeDropdown,
      setActiveFilter: handleActiveFilter,
      setAction: handleFilterObject,
      errMsg: errMsg,
      setErrMsg: setErrMsg,
      type: "multiFieldNumberSelect",
      customConditionFieldnameList: [...TasksFilterList],
      disableAddButton: disableContactFieldAddBtn
    }
  ];

  useEffect(() => {
    return () => {
      setActiveDropdown("");
      setErrMsg("");
    };
  }, []);

  return (
    <div className="accountFilterByOption">
      <HeaderAction />
      <div className="filterBody">
        {filterOption.map((item, index) => {
          const { keyValue = "", type = "" } = item || {};
          if (type === "multiSelectApply") {
            return <MultiSelectDropdown key={keyValue} {...item} />;
          } else if (type === "multiDateSelect") {
            return <ContactDateFilter key={keyValue} {...item} inlineOperator />;
          } else if (type === "multiFieldSelect") {
            return <ContactFieldFilter key={keyValue} {...item} inlineOperator />;
          } else if (type === "multiFieldNumberSelect") {
            return <ContactMetricsFilter key={keyValue} {...item} inlineOperator />;
          }
        })}
      </div>
      <FooterAction actionList={actionList} />
    </div>
  );
};

export default Main;
